import { useState, forwardRef, useImperativeHandle, useEffect } from "react";
import Modal from "react-bootstrap/Modal";

/**
 * @typedef {React.HTMLProps<HTMLButtonElement> & {cancelText: string}} cancelButtonProps
 * @typedef {React.HTMLProps<HTMLButtonElement> & {saveText: string}} saveButtonProps
 */

/**
 * @typedef {Object} Props
 * @property {boolean} [Props.initialShow] - Initial show state
 * @property {boolean} [Props.show] - Controlled show state
 * @property {boolean} [Props.closeButton]
 * @property {string} [Props.contentClassName]
 * @property {string} [Props.title]
 * @property {function():void} Props.onHide
 * @property {React.ReactNode} Props.children
 * @property {React.RefObject<{show: function():void, hide: function():void}>} ref
 * @property {cancelButtonProps} [Props.cancelButtonProps]
 * @property {saveButtonProps} [Props.saveButtonProps]
 */

/**
 * @param {Props} props
 */

function DialogModal(
  {
    initialShow,
    show: stateShow,
    onHide: handleOnHide,
    contentClassName,
    title,
    children,
    cancelButtonProps = {},
    saveButtonProps = {},
    closeButton = false,
    ...props
  },
  ref
) {
  const [show, setShow] = useState(initialShow || false);

  const handleClose = () => setShow(false);

  useEffect(() => {
    if (stateShow === undefined) return;

    setShow(stateShow);
  }, [stateShow]);

  useImperativeHandle(
    ref,
    () => ({
      show: () => setShow(true),
      hide: () => setShow(false),
    }),
    []
  );

  const { cancelText, ...restCancelProps } = cancelButtonProps;
  const { saveText, ...restSaveProps } = saveButtonProps;

  return (
    <Modal
      show={show}
      onHide={handleOnHide || handleClose}
      contentClassName={contentClassName}
      {...props}
    >
      <Modal.Header closeButton={closeButton}>
        <Modal.Title>{title || "Dialog Modal"}</Modal.Title>
      </Modal.Header>

      <Modal.Body>{children}</Modal.Body>

      <Modal.Footer>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={handleClose}
          {...restCancelProps}
        >
          {cancelText || "Cancel"}
        </button>

        <button
          type="button"
          className="btn btn-primary"
          onClick={handleClose}
          {...restSaveProps}
        >
          {saveText || "Submit"}
        </button>
      </Modal.Footer>
    </Modal>
  );
}

export default forwardRef(DialogModal);
